import { AfterViewInit, Component, Inject, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { ErrorScreenLayoutComponent } from '../shared/error-screen-layout.component';
import {
  ErrorScreenCopy,
  ErrorScreenCopyByLocale,
} from '../shared/error-screen-copy';
import { resolveErrorScreenLocale } from '../shared/error-screen-locale';
import { ErrorScreenTelemetryService } from 'src/app/core/services/error-screen-telemetry.service';
import { ErrorScreenRecoveryService } from 'src/app/core/services/error-screen-recovery.service';
import {
  ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY,
  ERROR_SCREEN_CAUSE_STATE_KEY,
  ErrorScreenRecoveryAction,
  normalizeArrivalReason,
} from 'src/app/core/domain/error-screen';
import { createUnicornIncidentCode } from 'src/app/core/domain/unicorn';

/**
 * Spec 051 (error-screen-parity).
 * Phase 3: FR-008 translated (en/es + English fallback), FR-009 no status-code
 * heading, FR-010 independent of the failing application.
 * Phase 2: FR-005/FR-006 — a 5xx is usually a transient backend outage that ALSO
 * breaks the app's start, so "go home" alone is a dead end. The PRIMARY action
 * is a fresh retry that succeeds once the outage clears; "go to my start" is a
 * secondary escape, never the only action. FR-012 records whether it worked.
 */
export const PAGE_ERROR_500_COPY = {
  en: {
    logoAlt: 'rettX',
    heading: 'Something went wrong on our end',
    body:
      'We couldn’t finish what you were doing because of a problem on our side, not yours. It’s usually temporary — please try again in a moment.',
    retryLink: 'Try again',
    homeLink: 'Go to the home screen',
    imageAlt: 'Surprised unicorn',
  },
  es: {
    logoAlt: 'rettX',
    heading: 'Algo salió mal por nuestra parte',
    body:
      'No pudimos terminar lo que estabas haciendo por un problema nuestro, no tuyo. Suele ser temporal; inténtalo de nuevo en un momento.',
    retryLink: 'Intentar de nuevo',
    homeLink: 'Ir a la pantalla de inicio',
    imageAlt: 'Unicornio sorprendido',
  },
} satisfies ErrorScreenCopyByLocale;

@Component({
  selector: 'app-page-error-500',
  standalone: true,
  imports: [ErrorScreenLayoutComponent],
  templateUrl: './page-error-500.component.html',
  styleUrl: './page-error-500.component.scss',
})
export class PageError500Component implements AfterViewInit {
  private telemetry = inject(ErrorScreenTelemetryService);
  private recovery = inject(ErrorScreenRecoveryService);
  private recorded = false;
  readonly copy: ErrorScreenCopy;
  readonly incidentCode = createUnicornIncidentCode();
  private readonly arrivalReason: string;
  private readonly cause: unknown;

  constructor(@Inject(DOCUMENT) document: Document) {
    this.copy = PAGE_ERROR_500_COPY[resolveErrorScreenLocale(document)];
    const handoff = this.telemetry.readHandoffState();
    this.arrivalReason = normalizeArrivalReason(
      handoff?.[ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY],
    );
    this.cause = handoff?.[ERROR_SCREEN_CAUSE_STATE_KEY];
  }

  // Spec 051 FR-001/FR-002/FR-004: record this screen's appearance after render,
  // carrying any machine-readable reason handed over on arrival.
  ngAfterViewInit(): void {
    if (this.recorded) {
      return;
    }
    this.recorded = true;
    this.telemetry.recordScreenShown('server-error', {
      cause: this.cause,
      arrivalReason: this.arrivalReason,
    });
  }

  // Spec 051 Phase 2 FR-005/FR-006: `retry-reload` is the state-changing retry;
  // `go-home` is the secondary escape. Both target the start; the retry is a
  // fresh full-page load, so it re-attempts the operation the outage failed.
  recover(action: ErrorScreenRecoveryAction): void {
    this.recovery.recover({
      screen: 'server-error',
      action,
      incidentCode: this.incidentCode,
      arrivalReason: this.arrivalReason,
      destination: '/',
    });
  }
}
